interface BuildEndpointUrlWithTagsParams {
    /** Base URL, including any fixed query params that must always be freshly set (e.g. a
     * webhook's "id"/token or a start node's "postback_id") — never preserved from currentUrl. */
    baseUrl: string;
    /** Currently selected tag/variable names, each rendered as a "tag=REPLACE_ME" query param
     * unless a prior value for that tag already exists in currentUrl. */
    tags: string[];
    /** The previously generated endpoint URL (possibly manually edited by the user), read to
     * preserve tag values across regeneration instead of always resetting them to "REPLACE_ME". */
    currentUrl?: string;
}

/** Builds an endpoint URL by combining a base URL (with its own fixed params) with tag-based
 * query params, preserving any manually-edited tag values across regeneration — used by the CC
 * Webhook and Start node forms, whose "endpoint" fields work the same way. */
export const buildEndpointUrlWithTags = ({
    baseUrl,
    tags,
    currentUrl = "",
}: BuildEndpointUrlWithTagsParams): string => {
    let url: URL;
    try {
        url = new URL(baseUrl);
    } catch {
        return baseUrl;
    }

    const baseParamKeys = new Set(url.searchParams.keys());

    try {
        const existingUrl = new URL(currentUrl);
        existingUrl.searchParams.forEach((value, key) => {
            if (!baseParamKeys.has(key) && tags.includes(key)) {
                url.searchParams.set(key, value);
            }
        });
    } catch {
        // Ignore an empty/invalid currentUrl — nothing to preserve.
    }

    tags.forEach((tag) => {
        if (!url.searchParams.has(tag)) {
            url.searchParams.append(tag, "REPLACE_ME");
        }
    });

    return url.toString();
};
